/** Segunda a sexta da semana corrente (ou deslocada por `weekOffset`). Retorna ISO para a API. */
export function intervaloSemanaUtilIso(weekOffset = 0) {
  const { monday, friday } = datasSemanaUtil(weekOffset);
  return {
    inicio: monday.toISOString(),
    fim: friday.toISOString(),
    monday,
    friday,
  };
}

/** @returns {{ monday: Date, friday: Date }} */
export function datasSemanaUtil(weekOffset = 0) {
  const now = new Date();
  const dow = now.getDay();
  const diffToMonday = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() + diffToMonday + weekOffset * 7);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);
  friday.setHours(23, 59, 59, 999);
  return { monday, friday };
}

const DIAS_ROTULO = ["Seg", "Ter", "Qua", "Qui", "Sex"];

export function rotulosCincoDias(monday) {
  const out = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    const dia = String(d.getDate()).padStart(2, "0");
    const mes = String(d.getMonth() + 1).padStart(2, "0");
    out.push({
      key: `${d.getFullYear()}-${mes}-${dia}`,
      rotulo: DIAS_ROTULO[i],
      dataCurta: `${dia}/${mes}`,
      date: d,
    });
  }
  return out;
}

const DISPLAY_START_MIN = 8 * 60;
/** Fim da grade visível (18:00) — 10 h após as 8:00 */
const DISPLAY_END_MIN = 18 * 60;

/** @returns {number} minutos desde meia-noite local */
export function minutosDesdeMeiaNoite(d) {
  return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
}

function parseGoogleInstante(s) {
  if (s == null || s === "") return null;
  const str = String(s).trim();
  if (str.length <= 10) {
    const [y, m, day] = str.slice(0, 10).split("-").map((x) => parseInt(x, 10));
    if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null;
    return new Date(y, m - 1, day, 0, 0, 0, 0);
  }
  const t = Date.parse(str);
  if (Number.isNaN(t)) return null;
  return new Date(t);
}

function fimEventoAproximado(inicio, fimRaw) {
  const fim = parseGoogleInstante(fimRaw);
  if (fim) return fim;
  const ini = parseGoogleInstante(inicio);
  if (!ini) return null;
  const x = new Date(ini);
  x.setHours(x.getHours() + 1);
  return x;
}

function ymdLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** HH:mm (24h) em pt-BR */
function formatHoraCurta(d) {
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", hour12: false });
}

/** Título no bloco da grade: vazio ou placeholder do Google → "Ocupado". */
function tituloBlocoEvento(summary) {
  const t = summary == null ? "" : String(summary).trim();
  if (t === "") return "Ocupado";
  const norm = t.toLowerCase();
  if (norm === "(sem título)" || norm === "sem título") return "Ocupado";
  return t;
}

/** Calendário de recurso (sala) — não representa uma pessoa. */
function emailEhCalendarioRecurso(email) {
  if (email == null || email === "") return true;
  const s = String(email).trim().toLowerCase();
  return s.includes("@resource.calendar.google.com") || /^c_[a-z0-9]+@/i.test(s);
}

/**
 * Com `displayName` vazio (comum na API), monta nome a partir da parte local do e-mail.
 * Ex.: `maria.silva@empresa.com` → "Maria Silva"
 */
function nomeLegivelApartirEmail(email) {
  if (email == null || email === "") return "";
  const e = String(email).trim();
  if (emailEhCalendarioRecurso(e)) return "";
  const at = e.indexOf("@");
  const local = at >= 0 ? e.slice(0, at) : e;
  if (!local) return "";
  const parts = local.split(/[._+-]+/).filter((p) => p.length > 0);
  if (parts.length === 0) return "";
  return parts
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

/** Objeto `{ displayName?, email? }` (organizador, criador, participante). */
function nomeDePessoaCalendario(p) {
  if (p == null || typeof p !== "object") return "";
  const dn = p.displayName != null ? String(p.displayName).trim() : "";
  if (dn !== "") return dn;
  const em = p.email != null ? String(p.email).trim() : "";
  if (em === "") return "";
  const bonito = nomeLegivelApartirEmail(em);
  if (bonito !== "") return bonito;
  return emailEhCalendarioRecurso(em) ? "" : em;
}

/**
 * Quem “reserva” na UI: se o backend enviar `organizerLabel` (Calendar API não garante displayName),
 * usa-o; senão organizador (se for pessoa) → criador → participante humano.
 */
function rotuloOrganizadorEvento(ev) {
  if (ev == null || typeof ev !== "object") return "";

  const rotuloApi = ev.organizerLabel != null ? String(ev.organizerLabel).trim() : "";
  if (rotuloApi !== "") return rotuloApi;

  const org = ev.organizer;
  const cri = ev.creator;
  const attendees = Array.isArray(ev.attendees) ? ev.attendees : [];

  if (org && typeof org === "object") {
    const dn = org.displayName != null ? String(org.displayName).trim() : "";
    if (dn !== "") return dn;
    const em = org.email != null ? String(org.email).trim() : "";
    if (em && !emailEhCalendarioRecurso(em)) {
      return nomeLegivelApartirEmail(em) || em;
    }
  }

  const nCri = nomeDePessoaCalendario(cri);
  if (nCri) return nCri;

  for (let i = 0; i < attendees.length; i++) {
    const a = attendees[i];
    if (a == null || typeof a !== "object") continue;
    const em = a.email != null ? String(a.email).trim() : "";
    if (em && emailEhCalendarioRecurso(em)) continue;
    const n = nomeDePessoaCalendario(a);
    if (n) return n;
  }

  return "";
}

/**
 * E-mail do calendário de **sala** no Google (recurso de sala), ex. `c_...@resource.calendar.google.com`.
 * Não inclui outros participantes nem heurísticas amplas — só o recurso da própria sala.
 * @param {unknown} email
 * @returns {boolean}
 */
function emailEhCalendarioSalaGoogle(email) {
  if (email == null || email === "") return false;
  return String(email).trim().toLowerCase().endsWith("@resource.calendar.google.com");
}

/**
 * A **sala** (calendário de recurso Google) respondeu declined — não confunde com outro participante.
 * @param {unknown} ev
 * @returns {boolean}
 */
function recursoDaSalaDeclinou(ev) {
  if (ev == null || typeof ev !== "object") return false;
  const attendees = Array.isArray(ev.attendees) ? ev.attendees : [];
  for (let i = 0; i < attendees.length; i++) {
    const a = attendees[i];
    if (a == null || typeof a !== "object") continue;
    const em = a.email != null ? String(a.email).trim() : "";
    if (!emailEhCalendarioSalaGoogle(em)) continue;
    const rs = a.responseStatus != null ? String(a.responseStatus).toLowerCase() : "";
    if (rs === "declined") return true;
  }
  return false;
}

/**
 * Agrupa eventos por dia útil (chave ymd) com blocos posicionados na faixa 8:00–18:00.
 * @returns {Record<string, Array<{ topPct: number, heightPct: number, leftPct: number, widthPct: number, horario: string, summary: string, location: string, organizador: string, recursoDeclinou: boolean, detalhes: object }>>}
 */
export function eventosPorDiaGrade(eventos, monday) {
  const dias = [];
  for (let i = 0; i < 5; i++) {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    dias.push(ymdLocal(d));
  }

  const spanMin = DISPLAY_END_MIN - DISPLAY_START_MIN;
  const porDia = {};
  dias.forEach((k) => {
    porDia[k] = [];
  });

  const lista = Array.isArray(eventos) ? eventos : [];

  lista.forEach((ev) => {
    const ini = parseGoogleInstante(ev.start);
    const fim = fimEventoAproximado(ev.start, ev.end);
    if (!ini || !fim) return;

    dias.forEach((ymd) => {
      const [y, mo, da] = ymd.split("-").map((x) => parseInt(x, 10));
      const diaRef = new Date(y, mo - 1, da, 0, 0, 0, 0);
      const diaFim = new Date(y, mo - 1, da, 23, 59, 59, 999);

      const segIni = ini.getTime();
      const segFim = fim.getTime();
      if (segFim <= segIni) return;

      const overlapStart = Math.max(segIni, diaRef.getTime());
      const overlapEnd = Math.min(segFim, diaFim.getTime() + 1);
      if (overlapEnd <= overlapStart) return;

      const a = new Date(overlapStart);
      const b = new Date(overlapEnd);
      const win0 = new Date(diaRef);
      win0.setHours(8, 0, 0, 0);
      const win1 = new Date(diaRef);
      win1.setHours(18, 0, 0, 0);

      const clipA = Math.max(a.getTime(), win0.getTime());
      const clipB = Math.min(b.getTime(), win1.getTime());
      if (clipB <= clipA) return;

      const ca = new Date(clipA);
      const cb = new Date(clipB);
      const m0 = minutosDesdeMeiaNoite(ca);
      const m1 = minutosDesdeMeiaNoite(cb);
      const c0 = Math.max(m0, DISPLAY_START_MIN);
      const c1 = Math.min(m1, DISPLAY_END_MIN);
      if (c1 <= c0) return;

      const topPct = ((c0 - DISPLAY_START_MIN) / spanMin) * 100;
      const heightPct = ((c1 - c0) / spanMin) * 100;

      const hi = formatHoraCurta(ca);
      const hf = formatHoraCurta(cb);
      const horario = hi && hf ? `${hi} – ${hf}` : hi || hf || "";

      porDia[ymd].push({
        topPct,
        heightPct,
        horario,
        summary: tituloBlocoEvento(ev.summary),
        location: ev.location || "",
        organizador: rotuloOrganizadorEvento(ev),
        recursoDeclinou: recursoDaSalaDeclinou(ev),
        participantes: Number.isFinite(ev.participantes) ? ev.participantes : null,
        detalhes: {
          id: ev.id != null ? String(ev.id) : "",
          summary: tituloBlocoEvento(ev.summary),
          description: ev.description != null ? String(ev.description) : "",
          location: ev.location != null ? String(ev.location).trim() : "",
          htmlLink: ev.htmlLink != null ? String(ev.htmlLink) : "",
          hangoutLink: ev.hangoutLink != null ? String(ev.hangoutLink) : "",
          status: ev.status != null ? String(ev.status) : "",
          inicio: ca.toISOString(),
          fim: cb.toISOString(),
          organizador: rotuloOrganizadorEvento(ev),
          organizer: ev.organizer && typeof ev.organizer === "object" ? ev.organizer : null,
          creator: ev.creator && typeof ev.creator === "object" ? ev.creator : null,
          attendees: Array.isArray(ev.attendees) ? ev.attendees : [],
          recursoDeclinou: recursoDaSalaDeclinou(ev),
        },
      });
    });
  });

  atribuirLayoutHorizontalDia(porDia, spanMin);
  return porDia;
}

/**
 * Divide a largura da coluna do dia entre eventos simultâneos (colunas lado a lado).
 * @param {Record<string, Array<{ topPct: number, heightPct: number, leftPct?: number, widthPct?: number }>>} porDia
 * @param {number} spanMin
 */
function atribuirLayoutHorizontalDia(porDia, spanMin) {
  Object.keys(porDia).forEach((ymd) => {
    const blocos = porDia[ymd];
    const n = blocos.length;
    if (n === 0) return;

    /** @type {{ startMin: number, endMin: number, coluna: number, colunas: number }[]} */
    const meta = blocos.map((b) => {
      const startMin = DISPLAY_START_MIN + (b.topPct / 100) * spanMin;
      const endMin = startMin + (b.heightPct / 100) * spanMin;
      return { startMin, endMin, coluna: 0, colunas: 1 };
    });

    const sortedIdx = [...Array(n).keys()].sort((i, j) => {
      const d = meta[i].startMin - meta[j].startMin;
      if (d !== 0) return d;
      return meta[j].endMin - meta[i].endMin;
    });

    const colEnd = [];
    for (const idx of sortedIdx) {
      const m = meta[idx];
      let col = 0;
      while (col < colEnd.length && colEnd[col] > m.startMin) col++;
      if (col === colEnd.length) colEnd.push(m.endMin);
      else colEnd[col] = m.endMin;
      m.coluna = col;
    }

    const times = [];
    for (const m of meta) {
      times.push(m.startMin, m.endMin);
    }
    const uniqTimes = [...new Set(times)].sort((a, b) => a - b);

    for (let i = 0; i < n; i++) {
      const m = meta[i];
      let maxc = 1;
      for (let k = 0; k < uniqTimes.length - 1; k++) {
        const t = (uniqTimes[k] + uniqTimes[k + 1]) / 2;
        if (t <= m.startMin || t >= m.endMin) continue;
        let c = 0;
        for (let j = 0; j < n; j++) {
          const mj = meta[j];
          if (mj.startMin < t && mj.endMin > t) c++;
        }
        maxc = Math.max(maxc, c);
      }
      const mid = (m.startMin + m.endMin) / 2;
      {
        let c = 0;
        for (let j = 0; j < n; j++) {
          const mj = meta[j];
          if (mj.startMin < mid && mj.endMin > mid) c++;
        }
        maxc = Math.max(maxc, c);
      }
      m.colunas = maxc;
    }

    for (let i = 0; i < n; i++) {
      const m = meta[i];
      const cols = Math.max(1, m.colunas);
      const w = 100 / cols;
      blocos[i].widthPct = Math.round(w * 10000) / 10000;
      blocos[i].leftPct = Math.round(m.coluna * w * 10000) / 10000;
    }
  });
}

/**
 * Marcas de hora alinhadas à mesma escala linear dos blocos (8:00–18:00).
 * @returns {Array<{ label: string, topPct: number, isPrimeira: boolean, isUltima: boolean }>}
 */
export function marcasHorasParaGrade() {
  const span = DISPLAY_END_MIN - DISPLAY_START_MIN;
  const out = [];
  for (let h = 8; h <= 18; h += 1) {
    const m = h * 60;
    out.push({
      label: `${String(h).padStart(2, "0")}:00`,
      topPct: ((m - DISPLAY_START_MIN) / span) * 100,
    });
  }
  out.forEach((row, i) => {
    row.isPrimeira = i === 0;
    row.isUltima = i === out.length - 1;
  });
  return out;
}

/** Minutos: próximo evento a começar neste intervalo → estado "em breve" (tablet na porta). */
const EM_BREVE_MIN_PADRAO = 25;

/**
 * Estado da sala com base nos eventos do calendário e no relógio local.
 * Eventos em que o recurso da sala respondeu `declined` não entram (a sala não ocupa).
 * @param {unknown[]} eventos
 * @param {Date} agora
 * @param {{ emBreveMinutos?: number }} [opts]
 * @returns {{ codigo: 'livre' | 'em_breve' | 'ocupada', rotulo: string, detalhe: string }}
 */
export function estadoOcupacaoSala(eventos, agora, opts = {}) {
  const emBreveMs = (opts.emBreveMinutos ?? EM_BREVE_MIN_PADRAO) * 60 * 1000;
  const t = agora.getTime();
  const lista = Array.isArray(eventos) ? eventos : [];

  /** @type {Array<{ ini: Date, fim: Date }>} */
  const intervalos = [];
  for (const ev of lista) {
    if (recursoDaSalaDeclinou(ev)) continue;
    const ini = parseGoogleInstante(ev.start);
    const fim = fimEventoAproximado(ev.start, ev.end);
    if (!ini || !fim || fim.getTime() <= ini.getTime()) continue;
    intervalos.push({ ini, fim });
  }

  for (const { ini, fim } of intervalos) {
    if (t >= ini.getTime() && t < fim.getTime()) {
      return {
        codigo: "ocupada",
        rotulo: "Ocupada",
        detalhe: `Até ${formatHoraCurta(fim)}`,
      };
    }
  }

  let proximo = null;
  for (const { ini } of intervalos) {
    if (ini.getTime() > t) {
      if (!proximo || ini.getTime() < proximo.getTime()) proximo = ini;
    }
  }

  if (proximo && proximo.getTime() - t <= emBreveMs && proximo.getTime() > t) {
    return {
      codigo: "em_breve",
      rotulo: "Será ocupada em breve",
      detalhe: `Reserva às ${formatHoraCurta(proximo)}`,
    };
  }

  if (proximo) {
    return {
      codigo: "livre",
      rotulo: "Disponível agora",
      detalhe: `Próxima reserva: ${formatHoraCurta(proximo)}`,
    };
  }

  return {
    codigo: "livre",
    rotulo: "Disponível agora",
    detalhe: "Sem outras reservas no período carregado",
  };
}

/** `90 min` restantes → `1h 30min`; `< 1 min` → `"agora"`. */
function formatarDuracaoRestante(msRestante) {
  if (!Number.isFinite(msRestante) || msRestante <= 0) return "agora";
  const totalMin = Math.round(msRestante / 60000);
  if (totalMin <= 0) return "agora";
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h <= 0) return `${m} min`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
}

/**
 * Estado da sala para o **mosaico** (telão com várias salas): só 2 estados — `livre` / `ocupada`.
 * Ao contrário de {@link estadoOcupacaoSala} (usada no display da porta, com estado intermediário
 * "em breve"), aqui o texto sempre traz quanto tempo falta (contagem regressiva) além do horário.
 * Eventos em que o recurso da sala respondeu `declined` não entram (a sala não ocupa).
 * @param {unknown[]} eventos
 * @param {Date} agora
 * `tempoRotulo`, `tempoValor` e `hora` são as partes de `detalhe`, para o painel dar peso diferente a cada uma.
 * @returns {{ codigo: 'livre' | 'ocupada', rotulo: string, detalhe: string, organizador: string, tempoRotulo: string, tempoValor: string, hora: string }}
 */
export function estadoMosaicoSala(eventos, agora) {
  const t = agora.getTime();
  const lista = Array.isArray(eventos) ? eventos : [];

  /** @type {Array<{ ini: Date, fim: Date, organizador: string }>} */
  const intervalos = [];
  for (const ev of lista) {
    if (recursoDaSalaDeclinou(ev)) continue;
    const ini = parseGoogleInstante(ev.start);
    const fim = fimEventoAproximado(ev.start, ev.end);
    if (!ini || !fim || fim.getTime() <= ini.getTime()) continue;
    intervalos.push({ ini, fim, organizador: rotuloOrganizadorEvento(ev) });
  }

  for (const item of intervalos) {
    if (t >= item.ini.getTime() && t < item.fim.getTime()) {
      const duracao = formatarDuracaoRestante(item.fim.getTime() - t);
      const hora = formatHoraCurta(item.fim);
      return {
        codigo: "ocupada",
        rotulo: "Ocupada",
        organizador: item.organizador || "",
        detalhe: `Livre em ${duracao} (às ${hora})`,
        tempoRotulo: "Livre em",
        tempoValor: duracao,
        hora,
      };
    }
  }

  let proximo = null;
  for (const item of intervalos) {
    if (item.ini.getTime() > t && (!proximo || item.ini.getTime() < proximo.ini.getTime())) {
      proximo = item;
    }
  }

  if (proximo) {
    const duracao = formatarDuracaoRestante(proximo.ini.getTime() - t);
    const hora = formatHoraCurta(proximo.ini);
    return {
      codigo: "livre",
      rotulo: "Livre",
      organizador: "",
      detalhe: `Próxima reserva em ${duracao} (às ${hora})`,
      tempoRotulo: "Próxima reserva em",
      tempoValor: duracao,
      hora,
    };
  }

  return {
    codigo: "livre",
    rotulo: "Livre",
    organizador: "",
    detalhe: "Sem próximas reservas",
    tempoRotulo: "Sem próximas reservas",
    tempoValor: "",
    hora: "",
  };
}

/**
 * Link do Google Agenda para criar um evento já convidando a sala (calendário de recurso) —
 * ao salvar o convite, a reserva aparece nesta sala. Abre app ou web, conforme o dispositivo.
 * @param {string} googleCalendarId E-mail/ID do calendário de recurso da sala
 * @param {string} [nomeSala] Nome da sala — usado como sugestão de título/local do evento
 * @returns {string}
 */
export function linkCriarEventoGoogleCalendar(googleCalendarId, nomeSala) {
  const params = new URLSearchParams();
  params.set("action", "TEMPLATE");
  params.set("text", nomeSala ? `Reunião — ${nomeSala}` : "Reunião");
  if (nomeSala) params.set("location", nomeSala);
  const calId = googleCalendarId == null ? "" : String(googleCalendarId).trim();
  if (calId) params.set("add", calId);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Blocos de um único dia na faixa 8:00–18:00 (mesmo layout da grade do tablet, inclusive
 * eventos simultâneos lado a lado). Funciona também em sábado/domingo.
 * @param {unknown[]} eventos
 * @param {Date} dia
 */
export function blocosDoDiaGrade(eventos, dia) {
  const inicioDia = new Date(dia.getFullYear(), dia.getMonth(), dia.getDate());
  return eventosPorDiaGrade(eventos, inicioDia)[ymdLocal(inicioDia)] || [];
}

export { DISPLAY_START_MIN, DISPLAY_END_MIN };
